import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import backgroundImage from '../../assets/MWCPage.jpg';


const loginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setUser, setProfile, isAuthenticated } = useAuthStore();

  // Redirect if already logged in
  React.useEffect(() => {
    if (isAuthenticated) {
      console.log('✅ Already authenticated, redirecting to dashboard');
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (values: { email: string; password: string }) => {
    try {
      setError(null);
      console.log('🔐 Attempting login with:', values.email);
      
      const data = await authService.login(values);
      console.log('✅ Login response:', data);
      
      if (data.user) {
        setUser(data.user);
        console.log('👤 User set:', data.user.id);
        
        const profile = await authService.getProfile(data.user.id);
        console.log('📋 Profile fetched:', profile);
        
        setProfile(profile);
        toast.success('Welcome back!');
        
        // Redirect to dashboard after successful login
        console.log('🚀 Redirecting to dashboard...');
        navigate('/dashboard');
      } else {
        console.error('❌ No user in response');
        setError('Login failed: No user returned');
      }
    } catch (err: any) {
      console.error('❌ Login error:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.status,
        code: err.code,
        details: err
      });
      
      let errorMessage = 'Failed to login';
      
      if (err.message?.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password. Please check your credentials.';
      } else if (err.message?.includes('Email not confirmed')) {
        errorMessage = 'Please verify your email address before logging in.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        position: 'relative',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(138, 43, 226, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Paper
        elevation={24}
        sx={{
          maxWidth: 450,
          width: '100%',
          mx: 2,
          borderRadius: 4,
          position: 'relative',
          zIndex: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.25), 0 10px 30px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top 50% - Image Section */}
        <Box
          sx={{
            width: '100%',
            height: '250px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
            p: 3,
          }}
        >
          <Box
            component="img"
            src={backgroundImage}
            alt="MW Consultancy"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Bottom 50% - Form Section */}
        <Box sx={{ p: 4 }}>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
                backdropFilter: 'blur(10px)',
              }}
            >
              {error}
            </Alert>
          )}

          <Formik
              initialValues={{ email: '', password: '' }}
              validationSchema={loginSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form>
                <Box sx={{ mb: 2 }}>
                  <Field name="email">
                    {({ field }: any) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Email Address"
                        type="email"
                        variant="outlined"
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        placeholder="Enter your email"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(255, 255, 255, 0.98)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                              backgroundColor: 'white',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'white',
                              boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
                            },
                          },
                          '& .MuiOutlinedInput-input': {
                            color: '#000000 !important',
                            WebkitTextFillColor: '#000000 !important',
                          },
                          '& .MuiInputLabel-root': {
                            backgroundColor: 'white',
                            padding: '0 8px',
                            borderRadius: '4px',
                            fontWeight: 600,
                            color: '#333',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '2px',
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Field name="password">
                    {({ field }: any) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        placeholder="Enter your password"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                size="large"
                                aria-label="toggle password visibility"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(255, 255, 255, 0.98)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                              backgroundColor: 'white',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'white',
                              boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
                            },
                          },
                          '& .MuiOutlinedInput-input': {
                            color: '#000000 !important',
                            WebkitTextFillColor: '#000000 !important',
                          },
                          '& .MuiInputLabel-root': {
                            backgroundColor: 'white',
                            padding: '0 8px',
                            borderRadius: '4px',
                            fontWeight: 600,
                            color: '#333',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '2px',
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #653a8a 100%)',
                      boxShadow: '0 12px 28px rgba(102, 126, 234, 0.5)',
                    },
                  }}
                >
                  {isSubmitting ? 'Signing in...' : 'Sign In'}
                </Button>
                </Form>
              )}
            </Formik>

          <Typography
            variant="caption"
            sx={{
              mt: 3,
              display: 'block',
              textAlign: 'center',
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            © 2026 MW Consultancy. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};